import { def } from './nodeHelper'

interface CandidateInfo {
  email?: string
  name?: string
}

// 입력 Text 정규화: zwsp 제거 + 좌우 공백 제거
export const normalizeSearchText = (text: string): string =>
  text.replace(new RegExp(def.zwsp, 'g'), '').trim()

// 선택된 후보가 현재 입력 Text로 검색했을 때 나올 수 있는 값인지 재검증한다.
// 검색 매칭 규칙(SharedEmailSelect.checkValid 비-exact 경로)과 동일하게
// 후보의 email 또는 name 이 입력 Text 를 부분 문자열로 포함하는지로 판정.
export const canCandidateMatchInput = (
  inputText: string,
  candidate: CandidateInfo | null | undefined,
): boolean => {
  if (candidate == null) {
    return false
  }
  const text = normalizeSearchText(inputText).toLowerCase()
  if (text.length === 0) {
    return false
  }
  const email = (candidate.email ?? '').toLowerCase()
  const name = (candidate.name ?? '').toLowerCase()
  return email.includes(text) || name.includes(text)
}

// 검색 결과를 메뉴에 노출하기 전, 입력 Text와 무관한 항목(검색 소스가 부분 일치하지
// 않는 값을 반환하는 경우)을 걸러낸다. canCandidateMatchInput 과 동일 규칙.
export const filterCandidatesByInput = <T extends CandidateInfo>(
  inputText: string,
  candidates: T[],
): T[] => candidates.filter((candidate) => canCandidateMatchInput(inputText, candidate))

// 입력 주소가 내부 도메인(로그인 계정 도메인) 소속인지 판정한다.
// 대소문자를 구분하지 않는다 — `User@EXAMPLE.COM` 도 `@example.com` 소속으로 본다.
export const isInnerDomainEmail = (email: string, innerDomain?: string | null): boolean => {
  if (innerDomain == null || innerDomain.length === 0) {
    return false
  }
  return email.toLowerCase().endsWith(innerDomain.toLowerCase())
}

// 회사 주소록(디렉터리) 조회를 보낼 가치가 있는 주소인지 판정한다.
// 서버는 검색 범위를 호출자 도메인으로 한정하므로 타 도메인 주소는 항상 빈 결과다.
// innerDomain 을 알 수 없어 판정이 불가한 경우에는 종전대로 조회한다(기능 축소 방지).
export const shouldSearchDirectory = (email: string, innerDomain?: string | null): boolean => {
  if (innerDomain == null || innerDomain.length === 0) {
    return true
  }
  return isInnerDomainEmail(email, innerDomain)
}

interface MenuKeyItem {
  key: string
}

// OMD-165 — 자동완성 목록이 갱신될 때 유지할 focus key 를 결정한다.
// 기존 선택(selectedKeys[0])이 새 목록에 여전히 있으면 그 key 를 유지하고,
// 없거나 선택이 비어 있으면 첫 항목(menu[0].key)으로 초기화한다. 목록이 비면 null.
export const resolveFocusKey = <T extends MenuKeyItem>(
  menu: T[],
  selectedKeys: string[],
): string | null => {
  if (menu.length === 0) {
    return null
  }
  const current = selectedKeys[0]
  if (current != null && menu.some((item) => item.key === current)) {
    return current
  }
  return menu[0].key
}
